/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {ListMenuContent} from './list-menu-content'
import {Box, Text, useMultiStyleConfig, Fade} from '@chakra-ui/react'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {remCalc} from '../../theme/_utils'

const ListMenuPopover = ({
    primarycategory,
    categoriesData,
    selectedMenuItem,
    setSelectedMenuItem,
    onItemClick,
}) => {
    const isOpen = selectedMenuItem === primarycategory.uid
    const [delayedIsOpen, setDelayedIsOpen] = useState(isOpen)

    useEffect(() => {
        let timeout
        if (isOpen) {
            setDelayedIsOpen(true)
        } else {
            if (selectedMenuItem === null) {
                timeout = setTimeout(() => setDelayedIsOpen(false), 200) // 0.2 seconds = Fade exit duration
            } else {
                setDelayedIsOpen(true)
            }
        }
        return () => {
            if (timeout) clearTimeout(timeout)
        }
    }, [isOpen, selectedMenuItem])

    const styles = useMultiStyleConfig('KenwoodMenu')

    const {url, label, isAbsolute} = getGlobalLink({
        link: primarycategory?.redirects,
        categoriesData,
    })

    const categoryID =
        primarycategory?.redirects?.categorysfcc?.data?.[0]?.id ?? primarycategory.uid

    return (
        <Box
            sx={{
                '.hidden-menu': {
                    visibility: 'hidden',
                },
                '.visible-menu': {
                    visibility: 'visible',
                },
            }}
        >
            <Box {...styles.listMenuTriggerContainer}>
                <Box
                    {...styles.listMenuTriggerGroup}
                    _focusVisible={{
                        border: remCalc(3),
                        borderStyle: 'solid',
                        borderColor: 'button.border.accessibility-blue-dark',
                        outline: 'unset',
                        boxShadow: 'unset',
                    }}
                    onClick={() => {
                        if (isOpen) {
                            setSelectedMenuItem(null)
                        } else {
                            setSelectedMenuItem(primarycategory.uid)
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Space') {
                            e.preventDefault()
                            if (isOpen) {
                                setSelectedMenuItem(null)
                            } else {
                                setSelectedMenuItem(primarycategory.uid)
                            }
                        } else if (e.key === 'Escape') {
                            setSelectedMenuItem(null)
                        }
                    }}
                    role="button"
                    tabIndex="0"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={label}
                    id={`heading-${categoryID}`}
                >
                    <Text
                        {...styles.listMenuTriggerLink}
                        sx={isOpen ? styles.listMenuTriggerLinkActive : {}}
                        {...{name: label + ' __'}}
                    >
                        {label}
                    </Text>
                </Box>
            </Box>

            <Box
                data-testid={categoryID}
                className={delayedIsOpen ? 'visible-menu' : 'hidden-menu'}
            >
                {delayedIsOpen && selectedMenuItem === primarycategory.uid && (
                    <Box {...styles.popoverOverlay} onClick={() => setSelectedMenuItem(null)} />
                )}

                <Fade
                    in={isOpen}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        pointerEvents: isOpen ? 'auto' : 'none',
                    }}
                >
                    <Box {...styles.listMenuContent}>
                        <ListMenuContent
                            secondLevel={primarycategory?.second_level}
                            categoriesData={categoriesData}
                            categoryUrl={url}
                            isCategoryUrlAbsoulte={isAbsolute}
                            onItemClick={onItemClick}
                        />
                    </Box>
                </Fade>
            </Box>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    categoriesData: PropTypes.object,
    primarycategory: PropTypes.object,
    selectedMenuItem: PropTypes.string,
    setSelectedMenuItem: PropTypes.func,
    onItemClick: PropTypes.func,
}

export {ListMenuPopover}
